import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Plus, X, CalendarClock, ArrowRight, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { PhotoUploader } from '../../components/os/FurnitureShared';

const inputCls = 'w-full px-3 py-2.5 rounded-lg text-sm outline-none';
const inputStyle = { backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' };
const STATUS_META = {
  agendado: { label: 'Agendado', color: '#C9A66B' },
  datos_completos: { label: 'Datos completos', color: '#8B9F8D' },
  convertido: { label: 'Convertido', color: '#6B8F6B' },
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>{label}</label>
    {children}
  </div>
);

export default function OSIntake() {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const emptyForm = { nombre: '', email: '', telefono: '', direccion: '', tipo_propiedad: '', notas: '', cita_fecha: '', cita_hora: '' };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    try {
      const res = await authFetch('/api/intake/');
      if (res.ok) setItems(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authFetch('/api/intake/', { method: 'POST', body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Cliente agendado');
      setShowCreate(false);
      setForm(emptyForm);
      load();
      setSelected(data);
    } catch (err) { toast.error(err.message || 'Error'); }
    setSaving(false);
  };

  const savePlanos = async (planos) => {
    try {
      const res = await authFetch(`/api/intake/${selected.id}`, { method: 'PATCH', body: JSON.stringify({ planos }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setSelected(data);
      load();
    } catch (e) { toast.error(e.message || 'Error'); }
  };

  const convert = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`/api/intake/${selected.id}/convert`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success('Proyecto creado — arranca el pipeline');
      navigate(`/os/projects/${data.project_id}`);
    } catch (e) { toast.error(e.message || 'Error'); }
    setSaving(false);
  };

  const canEdit = user?.role === 'admin' || user?.role === 'designer';

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#C9A66B', borderTopColor: 'transparent' }} /></div>;

  return (
    <div data-testid="os-intake" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>
            <CalendarClock size={22} style={{ color: '#C9A66B' }} /> Intake · Agenda de Clientes
          </h1>
          <p className="text-sm mt-1" style={{ color: '#C9B8A8' }}>Agenda citas, captura datos y planos, y convierte a proyecto</p>
        </div>
        {canEdit && (
          <button data-testid="new-intake-btn" onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-light tracking-wide transition-all"
            style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}>
            <Plus size={16} /> Agendar cliente
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border p-12 text-center" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
          <CalendarClock size={40} className="mx-auto mb-4" style={{ color: '#E8E3D8' }} />
          <p className="text-sm font-light" style={{ color: '#C9B8A8' }}>No hay clientes agendados aún</p>
        </div>
      ) : (
        <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
          {items.map((it, i) => {
            const meta = STATUS_META[it.status] || STATUS_META.agendado;
            return (
              <button key={it.id} data-testid={`intake-row-${it.id}`} onClick={() => setSelected(it)}
                className={`w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-neutral-50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                style={{ borderColor: '#E8E3D8' }}>
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-light truncate" style={{ color: '#2D2D2D' }}>{it.nombre}</p>
                  <p className="text-[11px] mt-0.5" style={{ color: '#C9B8A8' }}>
                    {it.cita_fecha ? `Cita: ${it.cita_fecha}${it.cita_hora ? ` · ${it.cita_hora}` : ''}` : 'Sin cita agendada'}
                    {it.planos?.length > 0 && ` · ${it.planos.length} plano(s)`}
                  </p>
                </div>
                <span className="text-[10px] px-2.5 py-1 rounded-full" style={{ backgroundColor: meta.color + '20', color: meta.color }}>{meta.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="rounded-2xl border p-8 w-full max-w-lg shadow-xl my-8" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>{selected.nombre}</h2>
              <button onClick={() => setSelected(null)} style={{ color: '#C9B8A8' }}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm mb-4" style={{ color: '#2D2D2D' }}>
              {selected.email && <p><span className="text-xs" style={{ color: '#C9B8A8' }}>Email:</span><br />{selected.email}</p>}
              {selected.telefono && <p><span className="text-xs" style={{ color: '#C9B8A8' }}>Teléfono:</span><br />{selected.telefono}</p>}
              {selected.direccion && <p className="col-span-2"><span className="text-xs" style={{ color: '#C9B8A8' }}>Dirección:</span><br />{selected.direccion}</p>}
              {selected.tipo_propiedad && <p><span className="text-xs" style={{ color: '#C9B8A8' }}>Propiedad:</span><br />{selected.tipo_propiedad}</p>}
              {selected.cita_fecha && <p><span className="text-xs" style={{ color: '#C9B8A8' }}>Cita:</span><br />{selected.cita_fecha} {selected.cita_hora}</p>}
              {selected.notas && <p className="col-span-2 font-light"><span className="text-xs" style={{ color: '#C9B8A8' }}>Notas:</span><br />{selected.notas}</p>}
            </div>
            {canEdit && selected.status !== 'convertido' && (
              <div className="mb-5">
                <PhotoUploader uploadUrl={`/api/intake/${selected.id}/planos`} photos={selected.planos} onChange={savePlanos}
                  label="Planos y fotos del espacio" accept="image/*,.pdf" testId="intake-planos" />
              </div>
            )}
            {selected.status === 'convertido' ? (
              <button data-testid="goto-project-btn" onClick={() => navigate(`/os/projects/${selected.project_id}`)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium" style={{ backgroundColor: '#6B8F6B20', color: '#6B8F6B' }}>
                Ver proyecto <ArrowRight size={14} />
              </button>
            ) : canEdit && (
              <button data-testid="convert-intake-btn" onClick={convert} disabled={saving}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50"
                style={{ backgroundColor: '#C9A66B', color: '#fff' }}>
                <FileText size={14} /> Convertir a proyecto (arrancar pipeline)
              </button>
            )}
          </div>
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowCreate(false)}>
          <div className="rounded-2xl border p-8 w-full max-w-lg shadow-xl my-8" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Agendar Cliente</h2>
              <button onClick={() => setShowCreate(false)} style={{ color: '#C9B8A8' }}><X size={20} /></button>
            </div>
            <form onSubmit={create} className="space-y-4">
              <Field label="Nombre del cliente *">
                <input data-testid="intake-field-nombre" required value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} className={inputCls} style={inputStyle} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email"><input data-testid="intake-field-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Teléfono"><input data-testid="intake-field-telefono" value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Dirección"><input data-testid="intake-field-direccion" value={form.direccion} onChange={e => setForm({ ...form, direccion: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Tipo de propiedad">
                  <select data-testid="intake-field-tipo" value={form.tipo_propiedad} onChange={e => setForm({ ...form, tipo_propiedad: e.target.value })} className={inputCls} style={inputStyle}>
                    <option value="">Seleccionar...</option>
                    {['Residencia', 'Condominio', 'Hotel', 'Comercial', 'Otro'].map(o => <option key={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Fecha de cita"><input data-testid="intake-field-fecha" type="date" value={form.cita_fecha} onChange={e => setForm({ ...form, cita_fecha: e.target.value })} className={inputCls} style={inputStyle} /></Field>
                <Field label="Hora"><input data-testid="intake-field-hora" type="time" value={form.cita_hora} onChange={e => setForm({ ...form, cita_hora: e.target.value })} className={inputCls} style={inputStyle} /></Field>
              </div>
              <Field label="Notas"><textarea data-testid="intake-field-notas" rows={2} value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} className={`${inputCls} resize-none`} style={inputStyle} /></Field>
              <p className="text-[10px] font-light" style={{ color: '#C9B8A8' }}>Los planos se suben al abrir el registro después de crearlo.</p>
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 py-2.5 rounded-lg text-sm border" style={{ borderColor: '#E8E3D8', color: '#2D2D2D' }}>Cancelar</button>
                <button data-testid="intake-submit-btn" type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50" style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}>
                  {saving ? 'Guardando...' : 'Agendar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
